import { Delete } from '@mui/icons-material'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { DocComment } from '@/types/comments'
import { getIdleTime, prettifyDate } from '@/utils/time'
import CommentsAvatar from './CommentsAvatar'

type CommentListItemProps = {
  comment: DocComment
  /** Author display name, resolved up front by the list (see useGetAuthUsers). */
  authorName?: string | null
  /** Whether the current user authored this comment. Only they may delete it. */
  isOwn?: boolean
  onDeleteClick?: () => void
}

export default function CommentListItem({
  comment,
  authorName,
  isOwn = false,
  onDeleteClick,
}: CommentListItemProps) {
  const isDeleted = !!comment.deleted_at

  const relativeTime = comment.created_at
    ? `${getIdleTime(comment.created_at, false, false)} ago`
    : ''
  const absoluteTime = comment.created_at
    ? prettifyDate(comment.created_at)
    : ''

  return (
    <Stack direction="row" spacing={1.5} sx={{ px: 2, py: 0.75 }}>
      <CommentsAvatar name={authorName} />
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {authorName || 'Unknown user'}
          </Typography>
          {relativeTime && (
            <Tooltip title={absoluteTime} disableInteractive>
              <Typography variant="caption" color="text.secondary">
                {relativeTime}
              </Typography>
            </Tooltip>
          )}
          <Stack sx={{ flex: 1 }} />
          {isOwn && !isDeleted && (
            <Tooltip title="Delete comment" disableInteractive>
              <IconButton
                size="small"
                aria-label="Delete comment"
                onClick={onDeleteClick}
                sx={{
                  color: 'text.disabled',
                  '&:hover': { color: 'error.main' },
                  alignSelf: 'center',
                }}
              >
                <Delete sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {isDeleted ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
          >
            [deleted]
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {comment.body}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}
